import { envFlags, FLAGS } from '@thoughtspot/env-flags';
import { generateUUID } from '@thoughtspot/js-util';
import { create } from '@thoughtspot/logger';
import { getClusterId, getCurrentOrgId, getUserGuid, isAutomaticTranslation, } from '@thoughtspot/session-service';
import { useEffect, useState } from 'react';
import { lru } from 'tiny-lru';
import { getPreferredLocale } from './i18n';
const logger = create('dynamic-translation');
const MAX_CACHE_SIZE_PER_LANGUAGE = 1000;
const MAX_LANGUAGES = 7;
export const LOADING_TEXT = '<span style="display:inline-block;width:3.5em;height:1em;background:#f5f5f5;border-radius:4px;position:relative;overflow:hidden;vertical-align:middle"><span style="content:\'\';position:absolute;top:0;left:-150px;height:100%;width:150px;background:linear-gradient(90deg,rgba(255,255,255,0) 0,rgba(255,255,255,0.8) 50%,rgba(255,255,255,0) 100%);animation:shimmer 1.5s infinite"></span></span><style>@keyframes shimmer{100%{transform:translateX(calc(100% + 3.5em + 150px))}}</style>';
let chromeTranslate = null;
let loadPromise = null;
let gesturePromise = null;
const pendingTranslations = new Set();
const languageToCache = lru(MAX_LANGUAGES);
const translationCallbacks = new Set();
let debounceTimer = null;
let initializationAttempts = 0;
let isEditMode = false;
const isPlaywrightTest = () => envFlags.getValue(FLAGS.e2eTest.name) === true;
const isEdgeBrowser = () => {
    if (typeof navigator === 'undefined')
        return false;
    const userAgentData = navigator.userAgentData;
    if (userAgentData?.brands) {
        return userAgentData.brands.some((brand) => brand.brand === 'Microsoft Edge');
    }
    return /Edg(A|iOS)?\//.test(navigator.userAgent);
};
export function setEditModeForTranslation(editMode) {
    isEditMode = editMode;
}
export function resetTranslationState() {
    chromeTranslate = null;
}
export async function translateHtmlContentAsync(html) {
    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const textNodes = [];
        function collectTextNodes(node) {
            if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
                textNodes.push(node);
            }
            else {
                node.childNodes.forEach(child => collectTextNodes(child));
            }
        }
        collectTextNodes(doc.body);
        await Promise.all(textNodes.map(async (node) => {
            const originalText = node.textContent;
            if (originalText) {
                node.textContent =
                    (await chromeTranslate?.translate(originalText)) ??
                        originalText;
            }
        }));
        return doc.body.innerHTML;
    }
    catch (error) {
        logger.error('Error translating HTML content', error);
        return html;
    }
}
async function waitForUserGesture() {
    if (gesturePromise) {
        return gesturePromise;
    }
    gesturePromise = new Promise(resolve => {
        const handler = () => {
            document.removeEventListener('click', handler);
            document.removeEventListener('keypress', handler);
            document.removeEventListener('touchstart', handler);
            gesturePromise = null;
            resolve();
        };
        document.addEventListener('click', handler);
        document.addEventListener('keypress', handler);
        document.addEventListener('touchstart', handler);
    });
    return gesturePromise;
}
export const isInIframe = () => {
    try {
        return window.self !== window.top;
    }
    catch (e) {
        return true;
    }
};
export const initializeChromiumTranslate = async () => {
    if (!isAutomaticTranslation() ||
        isEditMode ||
        initializationAttempts >= 3) {
        return;
    }
    const preferredLocale = getPreferredLocale();
    let locale = preferredLocale.split('-')[0];
    if (locale === 'en' || !('Translator' in window)) {
        return;
    }
    if (isEdgeBrowser()) {
        return;
    }
    if (locale === 'nb')
        locale = 'no';
    const availability = await window.Translator?.availability({
        sourceLanguage: 'en',
        targetLanguage: locale,
    });
    if (availability !== 'available') {
        logger.info('Waiting for user gesture to download translation model');
        await waitForUserGesture();
    }
    if (loadPromise) {
        await loadPromise;
        return;
    }
    try {
        chromeTranslate = null;
        loadPromise = window.Translator.create({
            sourceLanguage: 'en',
            targetLanguage: locale,
            monitor(m) {
                m.addEventListener('downloadprogress', () => {
                });
            },
        });
        chromeTranslate = await loadPromise;
    }
    catch (error) {
        logger.error('Error initializing Chromium translate', error);
        initializationAttempts += 1;
    }
    finally {
        loadPromise = null;
    }
};
export async function translateText(text) {
    if (!isAutomaticTranslation() || isEditMode) {
        return text;
    }
    const preferredLocale = getPreferredLocale();
    const locale = preferredLocale.split('-')[0];
    let cache = languageToCache.get(locale);
    if (!cache) {
        cache = lru(MAX_CACHE_SIZE_PER_LANGUAGE);
        languageToCache.set(locale, cache);
    }
    const cached = cache.get(text);
    if (cached) {
        return cached;
    }
    if (loadPromise) {
        await loadPromise;
    }
    if (!chromeTranslate && locale !== 'en' && 'Translator' in window) {
        await initializeChromiumTranslate();
    }
    if (!chromeTranslate) {
        return text;
    }
    try {
        let translated = text;
        if (/<\/?[a-z][\s\S]*>/i.test(text)) {
            translated = await translateHtmlContentAsync(text);
        }
        else {
            translated = (await chromeTranslate?.translate(text)) ?? text;
        }
        cache.set(text, translated);
        return translated;
    }
    catch (error) {
        logger.error('Error translating text', error);
        cache.set(text, text);
        return text;
    }
}
export async function translateTextWithLLM(texts) {
    const preferredLocale = getPreferredLocale();
    const locale = preferredLocale.split('-')[0];
    let cache = languageToCache.get(locale);
    if (!cache) {
        cache = lru(MAX_CACHE_SIZE_PER_LANGUAGE);
        languageToCache.set(locale, cache);
    }
    const uncachedTexts = texts.filter(text => !cache.get(text));
    if (uncachedTexts.length === 0) {
        return;
    }
    uncachedTexts.forEach(text => {
        cache.set(text, LOADING_TEXT);
    });
    const query = `
        query LLMTranslation_translate($texts: [String!]!, $targetLang: String!, $sourceLang: String) {
            LLMTranslation_translate(texts: $texts, targetLang: $targetLang, sourceLang: $sourceLang) {
                texts
            }
        }
    `;
    try {
        const response = await fetch('/prism/?op=LLMTranslation_translate', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Request-Id': generateUUID(),
                'X-Thoughtspot-Tenant-Id': getClusterId(),
                'X-ThoughtSpot-Orgs': getCurrentOrgId()?.toString(),
                'X-Thoughtspot-User-Id': getUserGuid(),
            },
            credentials: 'same-origin',
            body: JSON.stringify({
                query,
                variables: {
                    texts: uncachedTexts,
                    targetLang: locale.toUpperCase(),
                    sourceLang: 'EN',
                },
                operationName: 'LLMTranslation_translate',
            }),
        });
        if (!response.ok) {
            logger.error('Prism translation API error', response.status, response.statusText);
            uncachedTexts.forEach(text => {
                cache.set(text, text);
            });
            return;
        }
        const result = await response.json();
        if (result?.errors) {
            logger.error('GraphQL translation error', result.errors);
            uncachedTexts.forEach(text => {
                cache.set(text, text);
            });
            return;
        }
        const translations = result?.data?.LLMTranslation_translate?.texts;
        if (Array.isArray(translations) &&
            translations.length === uncachedTexts.length) {
            uncachedTexts.forEach((text, index) => {
                cache.set(text, translations[index]);
            });
            return;
        }
    }
    catch (error) {
        logger.error('Error calling Prism translation API', error);
        uncachedTexts.forEach(text => {
            cache.set(text, text);
        });
    }
}
async function processPendingTranslations() {
    if (pendingTranslations.size === 0)
        return;
    const keys = Array.from(pendingTranslations);
    pendingTranslations.clear();
    const shouldUseLLM = isPlaywrightTest() ||
        isEdgeBrowser() ||
        isInIframe() ||
        (!chromeTranslate && !('Translator' in window));
    if (shouldUseLLM) {
        await translateTextWithLLM(keys);
    }
    else {
        await Promise.allSettled(keys.map(key => translateText(key)));
    }
    translationCallbacks.forEach(callback => callback());
}
export function dt(key) {
    if (!isAutomaticTranslation() ||
        isEditMode ||
        initializationAttempts >= 3) {
        return key;
    }
    const preferredLocale = getPreferredLocale();
    const locale = preferredLocale.split('-')[0];
    if (!key?.trim() || locale === 'en') {
        return key;
    }
    const cache = languageToCache.get(locale);
    if (cache) {
        const cached = cache.get(key);
        if (cached)
            return cached;
    }
    pendingTranslations.add(key);
    if (debounceTimer) {
        clearTimeout(debounceTimer);
    }
    debounceTimer = setTimeout(() => {
        debounceTimer = null;
        processPendingTranslations();
    }, 250);
    return LOADING_TEXT;
}
export function useDynamicTranslation() {
    const [, forceUpdate] = useState({});
    useEffect(() => {
        const reRenderCallback = () => forceUpdate({});
        translationCallbacks.add(reRenderCallback);
        return () => {
            translationCallbacks.delete(reRenderCallback);
            if (translationCallbacks.size === 0 && debounceTimer) {
                clearTimeout(debounceTimer);
                debounceTimer = null;
            }
        };
    }, []);
    return { dt };
}
//# sourceMappingURL=dynamic-translation.js.map